import ErrorBoundary from 'components/common/ErrorBoundary/ErrorBoundary';
import Slider, { marks } from 'components/common/Slider/Slider';
import classes from 'components/widgets/HighLevelProgram/HighLevelProgram.module.scss';
import React, { useState } from 'react';
import { highLevelFilterInitialState, serialize } from 'components/widgets/HighLevelProgram/HighLevelProgram.utils';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { LoaderContainer } from 'components/common';
import NoData from 'components/common/NoData/noData';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import Lollipop from 'components/common/Lollipop/XaxisLollipop/lollipop';
import { useHighLevelProgramProvider } from 'components/widgets/HighLevelProgram/HighLevelProgram.provider';
import { useBaseWidget } from 'hooks/useBaseWidget';
import { useHighLevelProgramLogic } from 'components/widgets/HighLevelProgram/HighLevelProgram.logic';

const id = 'HighLevelProgram';
const componentKey = 'highLevelProgramLatest';
const title = 'High Level Program (Latest)';

const HighLevelProgram = ({
    projectId,
    contractId,
    latestVersionId,
    globalFilterData,
    widgetId,
    localFilters,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
}: IComponentProps) => {
    // Local State
    const [maxLevel, setMaxLevel] = useState<number[]>(highLevelFilterInitialState);

    // Provider
    const { ui, api } = useHighLevelProgramProvider({
        widgetId,
        contractId,
        projectId,
        latestVersionId,
        globalFilterData,
        localFilters,
        maxLevel,
    });

    // Widget
    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
    });

    // Logic
    const { max, handleHighLevelFilter, noDataText } = useHighLevelProgramLogic({ api, setMaxLevel });

    return (
        <ErrorBoundary>
            <WidgetWithTitle
                id={id}
                title={title}
                tooltip={api.highLevelProgram.data?.info}
                titleComponents={[
                    !widgetId && (
                        <div key={'SliderWrapper'} className={classes.selectWrapper}>
                            <div className={classes.title}>Max Level</div>
                            <Slider
                                key={'Slider'}
                                defaultValue={highLevelFilterInitialState}
                                step={1}
                                min={1}
                                max={max}
                                marks={marks(1, max)}
                                onChangeCommitted={(e, value) => handleHighLevelFilter(value)}
                                sx={{ width: '20rem' }}
                            />
                        </div>
                    ),
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddWidget
                        key={'AddWidget'}
                        componentKey={componentKey}
                        title={title}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                    />,
                    <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                    <ExportDropdown key={'export'} title={title} subTitle={subTitle} />,
                ]}
                titleFilters={[
                    editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                    widgetId && ui.pills.length > 0 ? (
                        <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={ui.pills} />
                    ) : null,
                ]}
            >
                {api.highLevelProgram.data?.data && api.highLevelProgram.data?.data.length > 0 && (
                    <div className={classes.chartContainer}>
                        <Lollipop
                            dataset={serialize(api.highLevelProgram.data.data)}
                            nowDate={api.highLevelProgram.data?.nowDate}
                            filter={maxLevel}
                        />
                    </div>
                )}
                {api.highLevelProgram.isLoading && !api.highLevelProgram.data && (
                    <LoaderContainer>
                        <OverlayWithSpinner />
                    </LoaderContainer>
                )}
                {!api.highLevelProgram.isLoading &&
                    (api.highLevelProgram.data?.data === null || api.highLevelProgram.data?.data.length === 0) && (
                        <NoData>{noDataText}</NoData>
                    )}
            </WidgetWithTitle>
        </ErrorBoundary>
    );
};

export default React.memo(HighLevelProgram);
